<template>

    <div>
        <div class="progress mb-4" style="height: 5px;">
            <div class="progress-bar" role="progressbar" :style="{ width: (currentStep / totalSteps * 100) + '%' }">
            </div>
        </div>

        <!-- Information de l'expéditeur connecté -->
        <div v-if="currentStep === 1" class="step-content">
            <h5 class="mb-3">Informations du destinataire</h5>
            <div class="form-group">
                <label class="badge">Nom et prénom</label>
                <input type="text" class="form-input-custom" v-model="formData.recipient.fullName">
            </div>
            <div class="form-row">
                <div class="form-group col-md-6">
                    <label class="badge">Téléphone</label>
                    <input type="tel" class="form-input-custom" v-model="formData.recipient.phone">
                </div>
                <div class="form-group col-md-6">
                    <label class="badge">Email</label>
                    <input type="email" class="form-input-custom" v-model="formData.recipient.email">
                </div>
            </div>
            
            <div class="form-group">
                <label class="badge">Adresse complète</label>
                <textarea class="form-input-custom" v-model="formData.recipient.address"></textarea>
            </div>
        </div>

        <!-- Step 2: Package Details -->
        <div v-if="currentStep === 2" class="step-content">
            <h5 class="mb-3">Détails du colis</h5>

            <!-- Choix de la méthode de calcul -->
            <div class="form-group">
                <label class="badge">Méthode de calcul</label>
                <div class="custom-radio-group">
                    <label class="custom-radio mr-3">
                        <input type="radio" value="kilos" v-model="formData.package.calculationMethod" />
                        <span class="radio-label">Par kilos</span>
                    </label>
                    <label class="custom-radio">
                        <input type="radio" value="cbm" v-model="formData.package.calculationMethod" />
                        <span class="radio-label">Par CBM</span>
                    </label>
                </div>
            </div>

            <!-- Champs CBM -->
            <div class="form-row" v-if="formData.package.calculationMethod === 'cbm'">
                <div class="form-group col-md-4">
                    <label class="badge">Longueur (cm)</label>
                    <input type="number" class="form-input-custom" v-model="formData.package.length">
                </div>
                <div class="form-group col-md-4">
                    <label class="badge">Largeur (cm)</label>
                    <input type="number" class="form-input-custom" v-model="formData.package.width">
                </div>
                <div class="form-group col-md-4">
                    <label class="badge">Hauteur (cm)</label>
                    <input type="number" class="form-input-custom" v-model="formData.package.height">
                </div>
            </div>

            <!-- Champ Poids -->
            <div class="form-row" v-if="formData.package.calculationMethod === 'kilos'">
                <div class="form-group col-md-4">
                    <label class="badge">Poids (kg)</label>
                    <input type="number" class="form-input-custom" v-model="formData.package.weight">
                </div>
            </div>

            <!-- Champ communs -->
            <div class="form-group">
                <label class="badge">Contenu/description</label>
                <textarea class="form-input-custom" rows="5" v-model="formData.package.content"></textarea>
            </div>
            <div class="form-group">
                <label class="badge">Photos du colis (optionnel)</label>
                <input type="file" class="form-control-file" @change="handleFileUpload">
            </div>
        </div>

        <!-- Step 3: Shipping Options -->
        <div v-if="currentStep === 3" class="step-content">
            <h5 class="mb-3">Options d'expédition</h5>
            <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>

            <div class="form-row">
                <div class="form-group col-md-6">
                    <label class="badge">Type de livraison</label>
                    <div class="select-container">
                        <select class="custom-select" v-model="formData.shipping.type">
                            <option value="standard">Standard</option>
                            <option value="express">Express</option>
                        </select>
                        <span class="arrow">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                class="lucide lucide-chevron-down">
                                <path d="m6 9 6 6 6-6" />
                            </svg>
                        </span>
                    </div>
                </div>

                <div class="form-group col-md-6">
                    <label class="badge">Ville de départ</label>
                    <div class="select-container">
                        <select class="custom-select" v-model="formData.shipping.sender_city">
                            <option value="" disabled selected>Sélectionnez une ville</option>
                            <option v-for="ville in villes" :key="ville.id" :value="ville.libelle">{{ ville.libelle }}
                            </option>
                        </select>
                        <span class="arrow">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                class="lucide lucide-chevron-down">
                                <path d="m6 9 6 6 6-6" />
                            </svg>
                        </span>
                    </div>
                </div>

                <div class="form-group col-md-6">
                    <label class="badge">Ville de destination</label>
                    <div class="select-container">
                        <select class="custom-select" v-model="formData.shipping.recipient_city">
                            <option value="" disabled selected>Sélectionnez une ville</option>
                            <option v-for="ville in villes" :key="`dest-${ville.id}`" :value="ville.libelle">{{
                                ville.libelle }}</option>
                        </select>
                        <span class="arrow">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                class="lucide lucide-chevron-down">
                                <path d="m6 9 6 6 6-6" />
                            </svg>
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Step 4: Confirmation -->
        <div v-if="currentStep === 4" class="step-content">
            <h5 class="mb-3">Confirmation</h5>
            <div class="card">
                <div class="card-body">
                    <h6>Résumé des informations</h6>
                    <div class="row">
                        <div class="col-md-6">
                            <h6 class="mb-2"><strong>Expéditeur (Vous)</strong></h6>
                            <p>{{ formData.sender.fullName }}<br>
                                {{ formData.sender.address }}<br>
                                {{ formData.sender.phone }}<br>
                                {{ formData.sender.email }}</p>
                        </div>
                        <div class="col-md-6">
                            <h6 class="mb-2"><strong>Destinataire</strong></h6>
                            <p>{{ formData.recipient.fullName }}<br>
                                {{ formData.recipient.address }}<br>
                                {{ formData.recipient.phone }}<br>
                                {{ formData.recipient.email }}</p>
                        </div>
                    </div>
                    <hr>
                    <h6 class="mb-2"><strong>Détails du colis</strong></h6>
                    <p>
                        <span v-if="formData.package.calculationMethod === 'cbm'">
                            Dimensions: {{ formData.package.length }}x{{ formData.package.width }}x{{
                                formData.package.height }} cm<br>
                        </span>
                        <span v-if="formData.package.calculationMethod === 'kilos'">
                            Poids: {{ formData.package.weight }} kg<br>
                        </span>
                        Description: {{ formData.package.content }}
                    </p>

                    <hr>
                    <h6 class="mb-2"><strong>Options d'expédition</strong></h6>
                    <p>Type: {{ formData.shipping.type }}<br>
                        Ville de départ: {{ formData.shipping.sender_city }}<br>
                        Ville de destination: {{ formData.shipping.recipient_city }}
                    </p>
                </div>
            </div>
        </div>

        <!-- Navigation buttons -->
        <div class="mt-4 d-flex justify-content-between">
            <button class="btn btn-secondary" @click="previousStep" v-if="currentStep > 1">Précédent</button>
            <button class="btn btn-rounded btn-primary" @click="nextStep" v-if="currentStep < totalSteps"
                :disabled="!isStepValid">Suivant</button>
            <button class="btn btn-rounded btn-success" @click="submitForm"
                v-if="currentStep === totalSteps">Confirmer</button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DeliveryStepsForm',
    data() {
        return {
            isLoading: false,
            showSuccess: false,
            currentStep: 1,
            totalSteps: 4, // Réduit de 5 à 4 étapes
            formData: {
                photo: null,
                sender: {
                    fullName: '',
                    address: '',
                    phone: '',
                    email: ''
                },
                recipient: {
                    fullName: '',
                    address: '',
                    phone: '',
                    email: ''
                },
                package: {
                    calculationMethod: 'kilos',
                    length: 0,
                    width: 0,
                    height: 0,
                    weight: 0,
                    content: '',
                    value: 0,
                },
                shipping: {
                    type: 'standard',
                    additionalServices: [],
                    pickupDate: '',
                    sender_city: '',
                    recipient_city: ''
                }
            },
            villes: []
        }
    },
    computed: {
        isStepValid() {
            switch (this.currentStep) {
                case 1: // Destinataire
                    return this.formData.recipient.fullName &&
                        this.formData.recipient.address &&
                        this.formData.recipient.phone &&
                        this.formData.recipient.email;
                case 2: // Détails du colis
                    return this.formData.package.content
                case 3: // Options d'expédition
                    return this.formData.shipping.type &&
                        this.formData.shipping.sender_city &&
                        this.formData.shipping.recipient_city
                default:
                    return true;
            }
        }
    },
    mounted() {
        this.getVilles();
        this.loadUserInfo();
    },
    methods: {
        loadUserInfo() {
            // Récupérer les informations de l'utilisateur connecté depuis le store
            const user = this.$store.getters.user;

            if (user) {
                this.formData.sender = {
                    fullName: user.nom && user.prenom ? `${user.nom} ${user.prenom}` : user.name,
                    address: user.adresse || '',
                    phone: user.telephone1 ? user.telephone1.toString() : '',
                    email: user.email || ''
                };
            }
        },
        getVilles() {
            this.isLoading = true
            this.axios.get('/api/villes').then((response) => {
                this.villes = response.data.data
                this.isLoading = false
            }).catch(() => {
                this.isLoading = false
                this.$toast.add({
                    severity: 'error',
                    summary: 'Probleme de connexion',
                    detail: 'Une erreur s\'est produite lors de la connexion au serveur !',
                    life: 5000
                });
            })
        },
        nextStep() {
            if (this.currentStep < this.totalSteps) {
                this.currentStep++;
            }
        },
        previousStep() {
            if (this.currentStep > 1) {
                this.currentStep--;
            }
        },
        handleFileUpload(event) {
            this.formData.photo = event.target.files;
        },
        reset() {
            this.currentStep = 1;

            // Garder les informations de l'expéditeur
            const senderInfo = { ...this.formData.sender };

            this.formData = {
                sender: senderInfo, // Conserver les informations de l'expéditeur
                recipient: {
                    fullName: '',
                    address: '',
                    phone: '',
                    email: ''
                },
                package: {
                    calculationMethod: 'kilos',
                    length: 0,
                    width: 0,
                    height: 0,
                    weight: 0,
                    content: '',
                    value: 0,
                    photos: null
                },
                shipping: {
                    type: 'standard',
                    additionalServices: [],
                    pickupDate: '',
                    sender_city: '',
                    recipient_city: ''
                }
            };
        },
        submitForm() {
            this.isLoading = true;

            const config = {
                headers: { 'Content-Type': 'multipart/form-data' }
            };

            const formDataPrime = new FormData();
            formDataPrime.append('description', this.formData.package.content);
            formDataPrime.append('client', this.formData.sender.fullName);
            formDataPrime.append('type_expedition', this.formData.shipping.type);
            formDataPrime.append('expediteur_nom', this.formData.sender.fullName);
            formDataPrime.append('expediteur_tel', this.formData.sender.phone);
            formDataPrime.append('expediteur_email', this.formData.sender.email);
            formDataPrime.append('expediteur_adresse', this.formData.sender.address);
            formDataPrime.append('ville_depart', this.formData.shipping.sender_city);
            formDataPrime.append('ville_arrivee', this.formData.shipping.recipient_city);
            formDataPrime.append('destinataire_nom', this.formData.recipient.fullName);
            formDataPrime.append('destinataire_tel', this.formData.recipient.phone);
            formDataPrime.append('destinataire_email', this.formData.recipient.email);
            formDataPrime.append('destinataire_adresse', this.formData.recipient.address);
            formDataPrime.append('details', JSON.stringify(this.formData.package));

            if (this.formData.photo) {
                formDataPrime.append('photo', this.formData.photo[0]);
            }

            try {
                this.axios.post('/api/demande-ramassages', formDataPrime, config).then(response => {
                    if (response.data.success === true) {
                        this.$toast.add({
                            severity: 'success',
                            detail: response.data.message,
                            life: 3000
                        });
                        this.reset()
                        this.isLoading = false;
                    }
                    else {
                        this.isLoading = false;
                        response.data.errors.forEach(element => {
                            this.$toast.add({
                                severity: 'warn',
                                summary: 'Oups !',
                                detail: element,
                                life: 7000
                            });
                        });
                    }
                }).catch((error) => {
                    this.isLoading = false;
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Erreur',
                        detail: 'Une erreur est survenue lors de l\'envoi du formulaire.',
                        life: 5000
                    });
                    console.error('Error submitting form:', error);
                });
            } catch (error) {
                this.isLoading = false;
                console.error('Error submitting form:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erreur',
                    detail: 'Une erreur est survenue lors de l\'envoi du formulaire.',
                    life: 5000
                });
            }
        }
    }
}
</script>

<style scoped>
.step-content {
    min-height: 200px;
}

.form-group {
    margin-bottom: 1rem;
}

.custom-radio-group {
    display: flex;
    gap: 20px;
    margin-top: 8px;
}

.custom-radio {
    display: flex;
    align-items: center;
    cursor: pointer;
}

.custom-radio input {
    margin-right: 8px;
}

.radio-label {
    font-size: 14px;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease-in-out;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.success-card {
    max-width: 400px;
    margin: 0 auto;
    border: 1px solid #28a745;
    background-color: #f8fff5;
}

/* Compatibilité avec les styles du formulaire principal */
.form-input-custom {
    background: #f0f2f5;
    border: none;
    border-radius: 8px;
    padding: 12px 16px;
    font-size: 14px;
    color: #333;
    width: 100%;
    -webkit-appearance: none;
    appearance: none;
}

/* Ajout des styles pour les sélecteurs dans le formulaire d'envoi de colis */
.select-container {
    position: relative;
    width: 100%;
}

.custom-select {
    background: #f0f2f5;
    border: none;
    border-radius: 8px;
    padding: 12px 40px 12px 16px;
    /* Équilibrage des paddings haut/bas */
    font-size: 14px;
    color: #1d1d1d;
    width: 100%;
    -webkit-appearance: none;
    appearance: none;
    cursor: pointer;
    line-height: 1.5;
    /* Ajout d'une hauteur de ligne */
    height: auto;
    /* Hauteur automatique basée sur le contenu */
}

.arrow {
    position: absolute;
    top: 50%;
    right: 12px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 25px;
    height: 25px;
    border-radius: 50%;
    border: 1.5px solid #ccc;
    pointer-events: none;
    transform: translateY(-50%);
}

.arrow svg {
    width: 15px;
    height: 15px;
    stroke: #333;
    stroke-width: 2;
}

/* Effet au survol */
.custom-select:hover,
.custom-select:focus {
    background: #e9ebef;
    outline: none;
    box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.5);
}

.form-input-custom:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.5);
    background: #e9ebef;
}

.btn-rounded {
    border-radius: 50px;
    padding: 10px 20px;
}

.highlight-btn {
    background-color: #3242C4;
    color: white;
    border: none;
    font-weight: 500;
}

.highlight-btn:hover {
    background-color: #2a37a0;
}
</style>